import React, { useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, IconSpinner, IconCollection } from '../../../components';
import { FormattedMessage, useIntl } from 'react-intl';
import { getEventTypeLabel } from '../../../util/data';
import { createSlug } from '../../../util/urlHelpers';
import classNames from 'classnames';
import { useSearchBox } from 'react-instantsearch';

import css from '../EventProviderDashboardPageForm.module.css';

// Highlight matching text
const highlightText = (text, searchQuery) => {
    if (!searchQuery || !text) return text;
    
    const regex = new RegExp(`(${searchQuery})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
        if (part.toLowerCase() === searchQuery.toLowerCase()) {
            return <span key={index} className={css.searchHighlight}>{part}</span>;
        }
        return part;
    });
};

const EventsDashboardContent = ({
  events,
  paginatedEvents,
  totalItems,
  currentPage,
  totalPages,
  shouldShowPagination,
  pageNumbers,
  handlePreviousPage,
  handleNextPage,
  handlePageClick,
  useInstantSearch,
  isSearching,
  searchValue,
  setSearchValue,
  currentUserId,
  listingIds,
  initiatedTransactions,
  getTransactionByListingId,
  eventTypes,
}) => {
  const history = useHistory();
  const intl = useIntl();
  
  // Use Algolia search box hook for instant search
  const { query, refine } = useSearchBox();
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  // Handle search input change with debouncing
  const handleSearchChange = (e) => {
    const nextValue = e.target.value;
    setSearchValue(nextValue);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      refine(nextValue);
    }, 300);
  };

  // Helper function to capitalize status
  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  // Action buttons for each event
  const actionButtonsMaybe = (event) => {
    const {
      id,
      title,
      actionType,
      rawHit,
    } = event || {};

    const isInitiatedTransactionForListing = listingIds.includes(id);
    const transaction = getTransactionByListingId(id, initiatedTransactions);

    const transactionStatus = rawHit?.transactionStatus;
    const acceptedTransaction = rawHit?.transaction;

    const isTransactionAcceptedForUser =
      transactionStatus === 'bidAccepted' &&
      (acceptedTransaction?.serviceProviderId === currentUserId || rawHit?.serviceProviderId === currentUserId);

    const actionButtonText = isInitiatedTransactionForListing ? 'View' : 'View Event';

    const hideActionButton = rawHit?.isExpired && rawHit?.isInquiryState;

    if (hideActionButton) {
      return null;
    }

    return (
      <div className={css.actionButtonsContainer}>
        <Button
          onClick={() => {
            if (isTransactionAcceptedForUser && acceptedTransaction?.orderId) {
              return history.push(`/sale/${acceptedTransaction.orderId}`);
            }
            if (isInitiatedTransactionForListing) {
              return history.push(`/order/${transaction.transactionId}`);
            }
            history.push(`/event/view/${createSlug(title)}/${id}`);
          }}
          className={`${css.actionButton} ${css[`actionButton${actionType.charAt(0).toUpperCase() + actionType.slice(1)}`]}`}
        >
          {actionButtonText}
        </Button>
      </div>
    );
  };

  return (
    <>
      <div className={css.eventDashboardFormContainer}>
        <div className={css.eventDashboardFormHeader}>
          <div className={css.eventDashboardFormTitleContainer}>
            <h1 className={css.eventDashboardFormTitle}>My Events</h1>
          </div>
          <div className={css.eventDashboardFormSearchContainer}>
            <div className={css.eventDashboardFormSearchInputContainer}>
              <IconCollection icon="searchIcon" iconClassName={css.searchIcon} />
              {useInstantSearch ? (
                <>
                  <input
                    type="text"
                    placeholder="Search events by title, type, or client..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    className={css.searchInput}
                  />
                  {searchValue && (
                    <button
                      className={css.clearSearchButton}
                      onClick={() => {
                        setSearchValue('');
                        refine('');
                      }}
                      aria-label="Clear search"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4L4 12M4 4L12 12" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  )}
                </>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Search events by title, type, or client..."
                    value={searchValue}
                    onChange={e => setSearchValue(e.target.value)}
                    className={css.searchInput}
                  />
                  {searchValue && (
                    <button
                      className={css.clearSearchButton}
                      onClick={() => setSearchValue('')}
                      aria-label="Clear search"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4L4 12M4 4L12 12" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>
            <div className={css.eventDashboardFormSearchButtonContainer}>
              <select className={css.eventFilterSelect}>
                <option value="all">All Events</option>
                <option value="active">Active Events</option>
                <option value="inactive">Inactive Events</option>
              </select>
              <IconCollection icon="dropdown_arrow" className={css.dropdownArrow} />
            </div>
          </div>
        </div>

        <div className={css.cardContainer}>
          {useInstantSearch && isSearching ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
              <IconSpinner />
            </div>
          ) : null}

          {/* Desktop Table View */}
          <div className={css.eventDashboardTableContainer}>
            <table className={css.eventDashboardTable}>
              <thead>
                <tr>
                  <th><FormattedMessage id="EventDashboardPage.eventDetails" /></th>
                  <th><FormattedMessage id="EventDashboardPage.client" /></th>
                  <th><FormattedMessage id="EventDashboardPage.dateAndTime" /></th>
                  <th><FormattedMessage id="EventDashboardPage.status" /></th>
                  <th><FormattedMessage id="EventDashboardPage.invoiceAmount" /></th>
                  <th><FormattedMessage id="EventDashboardPage.actions" /></th>
                </tr>
              </thead>
              <tbody>
                {paginatedEvents.map((event, index) => {
                  const {
                    id,
                    title,
                    type,
                    client,
                    date,
                    time,
                    status,
                    statusType,
                    invoiceAmount,
                    adjustment,
                    actionType,
                    rawHit
                  } = event || {};

                  const isInitiatedTransactionForListing = listingIds.includes(id);
                  const transaction = getTransactionByListingId(id, initiatedTransactions);
                  const eventTypeLabel = getEventTypeLabel(type);

                  const transactionStatus = rawHit?.transactionStatus;
                  const declinedTransactions = rawHit?.declinedTransactions;
                  const acceptedTransaction = rawHit?.transaction;

                  // Check if event is expired (biddingDeadline passed or within 2 hours and in inquiry state)
                  const biddingDeadline = rawHit?.biddingDeadline;
                  const nowMs = Date.now();
                  const twoHoursMs = 2 * 60 * 60 * 1000;
                  const isBiddingDeadlinePassed = biddingDeadline && nowMs > biddingDeadline;
                  const isWithinTwoHoursOfDeadline = biddingDeadline && (biddingDeadline - nowMs) <= twoHoursMs && (biddingDeadline - nowMs) > 0;
                  const isInquiryState = !isInitiatedTransactionForListing && transactionStatus !== 'bidAccepted' && transactionStatus !== 'bidDeclined';
                  const isExpired = isBiddingDeadlinePassed || (isWithinTwoHoursOfDeadline && isInquiryState);

                  // if event is declined for current user for particular event
                  const isTransactionDeclinedForUser =
                    transactionStatus === "bidDeclined" &&
                    declinedTransactions?.some(item => item.serviceProviderId === currentUserId);

                  const isTransactionAcceptedForUser =
                    transactionStatus === 'bidAccepted' &&
                    (acceptedTransaction?.serviceProviderId === currentUserId || rawHit?.serviceProviderId === currentUserId);

                  const isAcceptedForSomeoneElse =
                    transactionStatus === 'bidAccepted' && !isTransactionAcceptedForUser;

                  const shouldHideAcceptedForUser =
                    isAcceptedForSomeoneElse && !isInitiatedTransactionForListing;

                  const statusToShow = isExpired && isInquiryState
                    ? 'expired'
                    : isTransactionDeclinedForUser
                      ? 'declined'
                      : isTransactionAcceptedForUser
                        ? 'accepted'
                        : isAcceptedForSomeoneElse && isInitiatedTransactionForListing
                          ? 'declined'
                          : isInitiatedTransactionForListing
                            ? 'requested'
                            : status;

                  const eventTypeName = eventTypes?.find(item => item?.id === type)?.title || type;

                  return (
                    <tr key={`${event.id}-${index}`}>
                      <td>
                        <div className={css.eventDetails}>
                          <div className={css.eventTitle}>{highlightText(title, searchValue)}</div>
                          <div className={css.eventType}>{highlightText(eventTypeLabel, searchValue)}</div>
                          <div className={css.eventId}>{event.id}</div>
                        </div>
                      </td>
                      <td>
                        <div className={css.client}>{highlightText(client, searchValue)}</div>
                      </td>
                      <td>
                        <div className={css.dateTime}>
                          <div className={css.date}>{date}</div>
                          <div className={css.time}>{time}</div>
                        </div>
                      </td>
                      <td>
                        <span className={classNames(css.statusBadge, {
                          [css.statusBadgeDisputed]: statusToShow === "disputed",
                          [css.statusBadgeOpen]: statusToShow === "open",
                          [css.statusBadgeClosed]: statusToShow === "closed" || statusToShow === "expired",
                          [css.statusBadgeCompleted]: statusToShow === "completed",
                          [css.statusBadgeCompleted]: statusToShow === "accepted",
                          [css.statusBadgePending]: statusToShow === "pending",
                          [css.statusBadgePending]: statusToShow === "requested",
                          [css.statusBadgeDisputed]: statusToShow === "declined",
                        })}>
                          {capitalize(statusToShow)}
                        </span>
                      </td>
                      <td>
                        <div className={css.invoiceAmount}>
                          <div className={css.invoiceAmountValue}>{isInitiatedTransactionForListing ? `$${transaction.amount / 100}` : `${invoiceAmount}`}</div>
                          <div className={`${css.invoiceAdjustment} ${css[`invoiceAdjustment${statusType.charAt(0).toUpperCase() + statusType.slice(1)}`]}`}>
                            {adjustment}
                          </div>
                        </div>
                      </td>
                      <td>
                        {actionButtonsMaybe(event)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {useInstantSearch && !isSearching && totalItems === 0 ? (
            <div className={css.emptyState}>
              <div className={css.emptyStateContent}>
                <div className={css.emptyStateText}>
                  {searchValue ? (
                    <>
                      <div className={css.emptyStateTitle}>No events found</div>
                      <div className={css.emptyStateDescription}>
                        No events match "{searchValue}". Try adjusting your search or filters.
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={css.emptyStateTitle}>No events available</div>
                      <div className={css.emptyStateDescription}>
                        There are no events to display for the selected filter.
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Mobile Card View */}
          <div className={css.eventDashboardCardsView}>
            {paginatedEvents.map((event, index) => {
              const {
                id,
                title,
                type,
                client,
                date,
                time,
                status,
                statusType,
                invoiceAmount,
                adjustment,
                actionType,
                rawHit
              } = event || {};
              
              const isInitiatedTransactionForListing = listingIds.includes(id);
              const transaction = getTransactionByListingId(id, initiatedTransactions);
              const eventTypeLabel = getEventTypeLabel(type);

              const transactionStatus = rawHit?.transactionStatus;
              const declinedTransactions = rawHit?.declinedTransactions;
              const acceptedTransaction = rawHit?.transaction;

              // Check if event is expired (biddingDeadline passed or within 2 hours and in inquiry state)
              const biddingDeadline = rawHit?.biddingDeadline;
              const nowMs = Date.now();
              const twoHoursMs = 2 * 60 * 60 * 1000;
              const isBiddingDeadlinePassed = biddingDeadline && nowMs > biddingDeadline;
              const isWithinTwoHoursOfDeadline = biddingDeadline && (biddingDeadline - nowMs) <= twoHoursMs && (biddingDeadline - nowMs) > 0;
              const isInquiryState = !isInitiatedTransactionForListing && transactionStatus !== 'bidAccepted' && transactionStatus !== 'bidDeclined';
              const isExpired = isBiddingDeadlinePassed || (isWithinTwoHoursOfDeadline && isInquiryState);

              // if event is declined for current user for particular event
              const isTransactionDeclinedForUser =
                transactionStatus === "bidDeclined" &&
                declinedTransactions?.some(item => item.serviceProviderId === currentUserId);

              const isTransactionAcceptedForUser =
                transactionStatus === 'bidAccepted' &&
                (acceptedTransaction?.serviceProviderId === currentUserId || rawHit?.serviceProviderId === currentUserId);

              const isAcceptedForSomeoneElse =
                transactionStatus === 'bidAccepted' && !isTransactionAcceptedForUser;

              const shouldHideAcceptedForUser =
                isAcceptedForSomeoneElse && !isInitiatedTransactionForListing;

              if (shouldHideAcceptedForUser) {
                return null;
              }

              const actionButtonText = isInitiatedTransactionForListing ? 'View' : 'View Event';

              const statusToShow = isExpired && isInquiryState
                ? 'expired'
                : isTransactionDeclinedForUser
                  ? 'declined'
                  : isTransactionAcceptedForUser
                    ? 'accepted'
                    : isAcceptedForSomeoneElse && isInitiatedTransactionForListing
                      ? 'declined'
                      : isInitiatedTransactionForListing
                        ? 'requested'
                        : status;

              const hideActionButton = isExpired && isInquiryState;

              return (
                <div key={`${event.id}-${index}`} className={css.eventCard}>
                  <div className={css.eventCardHeader}>
                    <div className={css.eventCardTitleSection}>
                      <div className={css.eventCardTitle}>{event.title}</div>
                      <div className={css.eventCardType}>{eventTypeLabel}</div>
                      <div className={css.eventCardId}>{event.id}</div>
                    </div>
                    <div className={css.eventCardStatus}>
                      <span className={classNames(css.statusBadge, {
                        [css.statusBadgeDisputed]: statusToShow === "disputed",
                        [css.statusBadgeOpen]: statusToShow === "open",
                        [css.statusBadgeClosed]: statusToShow === "closed" || statusToShow === "expired",
                        [css.statusBadgeCompleted]: statusToShow === "completed",
                        [css.statusBadgeCompleted]: statusToShow === "accepted",
                        [css.statusBadgePending]: statusToShow === "pending",
                        [css.statusBadgePending]: statusToShow === "requested",
                        [css.statusBadgeDisputed]: statusToShow === "declined",
                      })}>
                        {capitalize(statusToShow)}
                      </span>
                    </div>
                  </div>
                  <div className={css.eventCardDetails}>
                    <div className={css.eventCardDetailRow}>
                      <div className={css.eventCardDetailLabel}><FormattedMessage id="EventDashboardPage.client" /></div>
                      <div className={css.eventCardDetailValue}>{event.client}</div>
                    </div>
                    <div className={css.eventCardDetailRow}>
                      <div className={css.eventCardDetailLabel}><FormattedMessage id="EventDashboardPage.dateAndTime" /></div>
                      <div>
                        <div className={css.eventCardDetailValue}>{event.date}</div>
                        <div className={css.eventCardDetailSubValue}>{event.time}</div>
                      </div>
                    </div>
                    <div className={css.eventCardDetailRow}>
                      <div className={css.eventCardDetailLabel}><FormattedMessage id="EventDashboardPage.invoiceAmount" /></div>
                      <div>
                        <div className={css.eventCardDetailValue}>{isInitiatedTransactionForListing ? `$${transaction.amount / 100}` : `${invoiceAmount}`}</div>
                        <div className={`${css.eventCardDetailSubValue} ${css[`invoiceAdjustment${event.statusType.charAt(0).toUpperCase() + event.statusType.slice(1)}`]}`}>
                          {adjustment}
                        </div>
                      </div>
                    </div>
                  </div>
                  {!hideActionButton && (
                    <div className={css.eventCardAction}>
                      <Button
                        onClick={() => {
                          if (isTransactionAcceptedForUser && acceptedTransaction?.orderId) {
                            return history.push(`/sale/${acceptedTransaction.orderId}`);
                          }
                          if (isInitiatedTransactionForListing) {
                            return history.push(`/order/${transaction.transactionId}`);
                          }
                          history.push(`/event/view/${createSlug(title)}/${id}`);
                        }}
                        className={`${css.actionButton} ${css[`actionButton${event.actionType.charAt(0).toUpperCase() + event.actionType.slice(1)}`]}`}
                      >
                        {actionButtonText}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {shouldShowPagination && (
            <div className={css.paginationContainer}>
              <button
                className={css.paginationArrow}
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <IconCollection icon="chevron_left" />
              </button>
              <div className={css.paginationNumbers}>
                {pageNumbers.map((pageNum, index) => {
                  if (typeof pageNum === 'number') {
                    const isActive = pageNum === currentPage;
                    return (
                      <span
                        key={pageNum}
                        className={`${css.paginationNumber} ${isActive ? css.paginationNumberActive : ''}`}
                        onClick={() => handlePageClick(pageNum)}
                        style={{ cursor: 'pointer' }}
                      >
                        {pageNum}
                      </span>
                    );
                  } else {
                    return (
                      <span key={`ellipsis-${index}`} className={css.paginationEllipsis}>
                        {pageNum}
                      </span>
                    );
                  }
                })}
              </div>
              <button
                className={css.paginationArrow}
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >
                <IconCollection icon="chevron_right" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EventsDashboardContent;
